import { useCallback, useEffect, useRef, useState } from 'react';
import { Field } from '@tw/tw-component-library';
import { useFormContext } from '../contexts/useFormContext';
import { createDebounce } from '../utils/debounce';

// Extended field type to include search extras
interface FieldWithSearch extends Field {
  extras?: Field['extras'] & {
    search?: {
      options?: string[];
      onSearch?: (searchTerm: string) => void;
      searchButton?: boolean;
      isOpenOverride?: boolean;
      setIsOpenOverride?: (isOpen: boolean) => void;
      isLoading?: boolean;
    };
  };
}

// Type for debounced function
type DebouncedSearchFunction = {
  (searchTerm: string, fieldName: string): void;
  cancel: () => void;
};

export const useSearchOperations = () => {
  const { formState, updateFormData } = useFormContext();
  // State for search operations
  const [searchOptions, setSearchOptions] = useState<Record<string, string[]>>(
    {}
  );
  const [dropdownStates, setDropdownStates] = useState<Record<string, boolean>>(
    {}
  );
  const [isSearchReady, setIsSearchReady] = useState(false);

  // Keep track of the last search term to prevent duplicate API calls
  const lastSearchTermRef = useRef<Record<string, string>>({});

  // Create a stable debounced search function reference
  const debouncedSearchRef = useRef<DebouncedSearchFunction | null>(null);

  // Initialize search options for known search fields
  useEffect(() => {
    const knownSearchFields = [
      'debitClientName',
      'debitMatterName',
      'creditClientName',
      'creditMatterName',
    ];

    setSearchOptions((prev) => {
      const newOptions = { ...prev };
      let hasChanges = false;

      knownSearchFields.forEach((fieldName) => {
        if (!(fieldName in newOptions)) {
          newOptions[fieldName] = [];
          hasChanges = true;
        }
      });

      return hasChanges ? newOptions : prev;
    });

    // Mark search as ready after initialization
    setIsSearchReady(true);
  }, []); // Run once on mount

  // Initialize the debounced function only once
  useEffect(() => {
    debouncedSearchRef.current = createDebounce(
      async (searchTerm: string, fieldName: string) => {
        // Skip if this exact search was already performed
        if (lastSearchTermRef.current[fieldName] === searchTerm) {
          return;
        }

        lastSearchTermRef.current[fieldName] = searchTerm;

        try {
          // For now, just set empty options since we're not handling clientMatter here
          setSearchOptions((prev) => ({
            ...prev,
            [fieldName]: [],
          }));
        } catch (error) {
          console.error(`Error fetching data for ${fieldName}:`, error);
          setSearchOptions((prev) => ({
            ...prev,
            [fieldName]: [],
          }));
        }
      },
      300
    );

    return () => {
      if (debouncedSearchRef.current?.cancel) {
        debouncedSearchRef.current.cancel();
      }
    };
  }, []);

  // Handle search for a field
  const handleSearch = useCallback(
    async (fieldName: string, searchTerm: string) => {
      if (!searchTerm || searchTerm.length < 3) {
        setSearchOptions((prev) => ({
          ...prev,
          [fieldName]: [],
        }));
        setDropdownStates((prev) => ({
          ...prev,
          [fieldName]: false,
        }));
        return;
      }

      // Open dropdown to show loading state
      setDropdownStates((prev) => ({
        ...prev,
        [fieldName]: true,
      }));

      // Use the debounced function to prevent rapid repeated searches
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current(searchTerm, fieldName);
      }
    },
    []
  );

  // Handle selection for a field
  const handleSelect = useCallback(
    (fieldName: string, selectedValue: string) => {
      // Update form data
      updateFormData({
        [fieldName]: selectedValue,
      });

      // Close dropdown
      setDropdownStates((prev) => ({
        ...prev,
        [fieldName]: false,
      }));
    },
    [updateFormData]
  );

  // Handle dropdown state changes
  const handleDropdownToggle = useCallback(
    (fieldName: string, isOpen: boolean) => {
      setDropdownStates((prev) => ({
        ...prev,
        [fieldName]: isOpen,
      }));
    },
    []
  );

  // Get display text function
  const getDisplayText = useCallback((option: string) => option, []);

  // Memoize the search props creation for each field
  const getSearchProps = useCallback(
    (field: FieldWithSearch, fieldName: string) => {
      const searchProps = {
        options: searchOptions[fieldName] || [],
        placeholder: field.placeholder || `Search for ${field.label}...`,
        onSelect: (option: string | null) => {
          if (option) {
            handleSelect(fieldName, option);
          } else {
            updateFormData({ [fieldName]: '' });
          }
        },
        onSearch: (searchTerm: string) => handleSearch(fieldName, searchTerm),
        searchButton: field.extras?.search?.searchButton ?? false,
        value: formState.data[fieldName] || '',
        isOpenOverride: dropdownStates[fieldName] || false,
        setIsOpenOverride: (isOpen: boolean) =>
          handleDropdownToggle(fieldName, isOpen),
        isLoading: false,
        getDisplayText,
        disabled: field.disabled,
        name: fieldName,
        id: fieldName,
      };

      return searchProps;
    },
    [
      searchOptions,
      handleSelect,
      handleSearch,
      formState.data,
      dropdownStates,
      handleDropdownToggle,
      getDisplayText,
      updateFormData,
    ]
  );

  // Process search field
  const processSearchField = useCallback(
    (field: FieldWithSearch) => {
      if (field.type === 'search') {
        const searchProps = getSearchProps(field, field.name);

        return {
          ...field,
          value: formState.data[field.name] || '',
          extras: {
            ...field.extras,
            search: searchProps,
          },
        };
      }
      return field;
    },
    [formState.data, getSearchProps]
  );

  return {
    processSearchField,
    searchState: {
      searchOptions,
      loadingFields: {},
      dropdownStates,
    },
    isSearchReady,
  };
};
